import BaseComponent from '../base-component/base-component'
import { checkEnterKey } from '../../utils/utils'
import CarouselDots from './carousel-dots'
import Carousel from './carousel'
import { Nullable } from '../../types/common'

class CarouselDot extends BaseComponent {
  autoplayLoader: Nullable<HTMLElement>

  constructor() {
    super()

    this.autoplayLoader = this.querySelector('[data-autoplay-indicator]')
  }

  connectedCallback(): void {
    this.updateAutoplayIndicator()

    if (this.carousel) {
      customElements.upgrade(this.carousel) // https://developer.mozilla.org/en-US/docs/Web/API/CustomElementRegistry/upgrade
      customElements.whenDefined('carousel-component').then(() => {
        this.carousel.subscribe(
          'changePlayState',
          this.handleCarouselChangePlayState,
        )
        this.carousel.subscribe(
          'forceChangePlayState',
          this.handleCarouselForceChangePlayState,
        )
      })
    }

    this.addEventListener('keydown', this.handleKeyDown)
    this.addEventListener('click', this.handleDotClick)
  }

  disconnectedCallback(): void {
    if (this.carousel) {
      customElements.whenDefined('carousel-component').then(() => {
        this.carousel.unsubscribe(
          'changePlayState',
          this.handleCarouselChangePlayState,
        )
        this.carousel.unsubscribe(
          'forceChangePlayState',
          this.handleCarouselForceChangePlayState,
        )
      })
    }

    this.removeEventListener('keydown', this.handleKeyDown)
    this.removeEventListener('click', this.handleDotClick)
  }

  handleCarouselForceChangePlayState = (): void => {
    if (!this.autoplayLoader || !this.carousel) {
      return
    }

    const isForceStopped = this.carousel.isForceStopped

    this.autoplayLoader.setAttribute(
      'data-autoplay-stopped',
      isForceStopped ? 'true' : 'false',
    )
  }

  handleCarouselChangePlayState = (): void => {
    this.updateAutoplayIndicator()
  }

  handleDotClick = (): void => {
    this.scroll()
  }

  handleKeyDown = (event: KeyboardEvent): void => {
    const isEnterKey = checkEnterKey(event.key || event.keyCode)

    if (isEnterKey) {
      event.preventDefault()

      this.scroll()
    }
  }

  updateAutoplayIndicator(): void {
    if (!this.carousel) {
      return
    }

    const isPlay = this.carousel.isPlay

    if (this.autoplayLoader) {
      this.autoplayLoader.setAttribute(
        'data-play-state',
        isPlay ? 'true' : 'false',
      )
    }
  }

  scroll(): void {
    if (!this.carousel) {
      return
    }

    const index = +this.dataset.dotIndex

    this.carousel.embla.scrollTo(index)
    this.carousel.forceStop()
  }

  get carousel() {
    return this.closest<Carousel>('carousel-component')
  }

  get dotsCarousel() {
    return this.closest<CarouselDots>('carousel-dots')
  }
}

export default CarouselDot
